/**
 * What is kept for this client that is not work: their brand files, the
 * references they sent, the links somebody wanted to remember.
 *
 * Shown on their page rather than only on the Buckets screen, because "where
 * did they send us that logo" is a question asked while looking at the client,
 * not while looking at a list of piles.
 */

import { Link2, Package, Paperclip, Plus, StickyNote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BentoCard } from '../../components/BentoCard';

import { useApi } from '../../useApi';
import type { Bucket } from '../buckets/types';

export function ClientBuckets({ clientId, clientName }: { clientId: string; clientName: string }) {
  const { result } = useApi<Bucket[]>('/buckets', { query: { client: clientId } });
  const buckets = result.data ?? [];

  return (
    <BentoCard
      title="Buckets"
      description={`Links, notes and files kept for ${clientName}`}
      action={
        <Link
          to="/buckets"
          className="flex h-8 items-center gap-1.5 rounded-lg border border-shell-border bg-shell px-3 text-xs font-medium text-shell-text-active transition-colors hover:bg-shell-border/40"
        >
          <Plus size={14} /> New
        </Link>
      }
    >
      {result.state === 'loading' && buckets.length === 0 ? (
        <p className="text-sm text-shell-text">Loading…</p>
      ) : buckets.length === 0 ? (
        <p className="text-sm text-shell-text">
          Nothing kept for them yet. A bucket holds the things that are not work — brand files, references, links
          worth finding again.
        </p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {buckets.map((b) => (
            <li key={b.id}>
              <Link
                to={`/buckets/${b.id}`}
                className="flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-shell-border/30"
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-brand-500/10 text-brand-500">
                  <Package size={15} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-shell-text-active">{b.name}</span>
                  {b.purpose !== null && b.purpose !== '' && (
                    <span className="block truncate text-xs text-shell-text">{b.purpose}</span>
                  )}
                </span>
                <span className="flex shrink-0 items-center gap-2.5 text-xs text-ink-500">
                  {b.links > 0 && (
                    <span className="flex items-center gap-1 tabular-nums">
                      <Link2 size={12} /> {b.links}
                    </span>
                  )}
                  {b.notes > 0 && (
                    <span className="flex items-center gap-1 tabular-nums">
                      <StickyNote size={12} /> {b.notes}
                    </span>
                  )}
                  {b.files > 0 && (
                    <span className="flex items-center gap-1 tabular-nums">
                      <Paperclip size={12} /> {b.files}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </BentoCard>
  );
}
